import React from 'react';
import PropTypes from 'prop-types';
import {
  Box, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { formatBRL } from '../../UI';
import * as C from '../../../theme/colors.constants';

/**
 * Grid "Antecedentes Ativos" — mirror SOMENTE LEITURA (sem checkbox, sem
 * ações de linha) de `antecedentes`, o MESMO dado já buscado para a aba
 * Acompanhamento (AW2-1033), sem fetch novo. Ajuste pedido pelo usuário
 * pós-Etapa 4: esta grid não é mais um mecanismo de seleção — só exibição;
 * a réplica de Anotar/Enviar/Agendar Compromisso sobre antecedentes (Etapas
 * 5/8/9) precisará de um mecanismo de seleção próprio, a decidir quando
 * essas etapas forem implementadas (o `antecedentesSelecionadosHistorico`
 * introduzido na Etapa 4 foi removido do container por ficar sem nenhum
 * consumidor — não deixar estado morto para trás).
 *
 * Todos os campos do DTO de antecedente (mesmo conjunto de
 * `TableAntecedentesProcesso.jsx` real, MENOS a coluna "Ações" — não faz
 * sentido oferecer Inspecionar/Resumo numa grid somente-leitura) — mais
 * colunas do que cabem confortavelmente na largura do card, por isso o
 * `Box` externo tem `overflowX: auto` e a `Table` um `minWidth` que força o
 * scroll horizontal a aparecer em vez de espremer as colunas.
 *
 * Header e corpo iteram sobre o MESMO array `COLUNAS` (regra já documentada
 * em `src/prototipo/CLAUDE.md` para evitar drift entre os dois).
 */
const COLUNAS = [
  { campo: 'numero', label: 'Número' },
  { campo: 'acordoAtivo', label: 'Acordo Ativo', align: 'center' },
  { campo: 'fase', label: 'Status' },
  { campo: 'negociador', label: 'Responsável' },
  { campo: 'dataAbertura', label: 'Data de Abertura' },
  { campo: 'cliente', label: 'Cliente' },
  { campo: 'devedor', label: 'Devedor' },
  { campo: 'valorProcesso', label: 'Valor do Processo', align: 'right' },
];

function renderizarValorColuna(antecedente, coluna) {
  if (coluna.campo === 'acordoAtivo') {
    return antecedente.acordoAtivo ? <CheckCircleIcon fontSize="small" color="success" /> : '';
  }
  if (coluna.campo === 'valorProcesso') {
    return antecedente.valorProcesso == null ? '—' : formatBRL(antecedente.valorProcesso);
  }
  return antecedente[coluna.campo];
}

export default function TableAntecedentesAtivos({ antecedentes }) {
  if (!antecedentes) {
    return (
      <Typography variant="body2" sx={{ color: C.warmGrey, textAlign: 'center', py: 3 }}>
        Carregando...
      </Typography>
    );
  }

  if (antecedentes.length === 0) {
    return (
      <Box
        sx={{
          backgroundColor: C.paleGrey,
          border: `1px solid ${C.borderGrey}`,
          borderRadius: 1.5,
          p: 2,
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" sx={{ color: C.warmGrey }}>
          Nenhum antecedente encontrado.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 760 }}>
        <TableHead>
          <TableRow>
            {COLUNAS.map(coluna => (
              <TableCell key={coluna.campo} align={coluna.align} sx={{ fontWeight: 700 }}>
                {coluna.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {antecedentes.map(antecedente => (
            <TableRow key={antecedente.id}>
              {COLUNAS.map(coluna => (
                <TableCell key={coluna.campo} align={coluna.align}>
                  {renderizarValorColuna(antecedente, coluna)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}
TableAntecedentesAtivos.propTypes = {
  antecedentes: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    numero: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    acordoAtivo: PropTypes.bool,
    fase: PropTypes.string,
    negociador: PropTypes.string,
    dataAbertura: PropTypes.string,
    cliente: PropTypes.string,
    devedor: PropTypes.string,
    valorProcesso: PropTypes.number,
  })),
};
TableAntecedentesAtivos.defaultProps = {
  antecedentes: null,
};
